import React, { useState, useRef, useCallback } from 'react';
import { UploadCloud, X, CheckCircle, AlertCircle, Loader, FileText, AlertTriangle } from 'lucide-react';
import { auth } from '../../../firebase/firebase.config';
import { importLeadsFromCSV, parseCSV, validateAllRows, MAX_FILE_SIZE } from '../../../firebase/csvImport.service';

/**
 * Mobile modal for importing leads from CSV files.
 * Full-featured: file select, preview, validation, progress, results.
 */
export const MobileImportModal = ({ onClose, onImportComplete }) => {
    const fileInputRef = useRef(null);

    // State machine: idle → preview → importing → results
    const [phase, setPhase] = useState('idle');

    // File and parsed data
    const [file, setFile] = useState(null);
    const [parsedData, setParsedData] = useState(null);
    const [validationResult, setValidationResult] = useState(null);

    // Import progress and results
    const [progress, setProgress] = useState({ current: 0, total: 0, phase: '' });
    const [results, setResults] = useState(null);
    const [error, setError] = useState('');

    // ========== FILE HANDLING ==========

    const handleFileSelect = useCallback(async (selectedFile) => {
        if (!selectedFile) return;

        if (!selectedFile.name.endsWith('.csv')) {
            setError('Please select a CSV file');
            return;
        }

        if (selectedFile.size > MAX_FILE_SIZE) {
            setError(`File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB`);
            return;
        }

        setError('');
        setFile(selectedFile);
        setPhase('preview');

        const parseResult = await parseCSV(selectedFile);
        if (!parseResult.success) {
            setError(parseResult.error);
            setPhase('idle');
            setFile(null);
            return;
        }

        setParsedData(parseResult.data);
        const validation = validateAllRows(parseResult.data);
        setValidationResult(validation);
    }, []);

    const handleInputChange = useCallback((e) => {
        if (e.target.files && e.target.files[0]) {
            handleFileSelect(e.target.files[0]);
        }
    }, [handleFileSelect]);

    const handleBrowseClick = () => {
        fileInputRef.current?.click();
    };

    // ========== IMPORT HANDLING ==========

    const handleImport = async () => {
        const userId = auth.currentUser?.uid;
        if (!userId) {
            setError('Please sign in to import leads');
            return;
        }

        if (!file || !validationResult || validationResult.validLeads.length === 0) {
            setError('No valid leads to import');
            return;
        }

        setPhase('importing');
        setProgress({ current: 0, total: validationResult.validLeads.length, phase: 'starting' });

        try {
            const importResult = await importLeadsFromCSV(userId, file, (prog) => {
                setProgress(prog);
            });

            setResults(importResult);
            setPhase('results');

            if (onImportComplete) {
                onImportComplete(importResult);
            }
        } catch (err) {
            console.error('[MobileImportModal] Import error:', err);
            setError(err.message || 'Import failed');
            setPhase('preview');
        }
    };

    // ========== RESET ==========

    const handleReset = () => {
        setPhase('idle');
        setFile(null);
        setParsedData(null);
        setValidationResult(null);
        setProgress({ current: 0, total: 0, phase: '' });
        setResults(null);
        setError('');
    };

    // ========== RENDERING ==========

    // Idle state
    const renderIdle = () => (
        <>
            <div className="text-center mb-6">
                <div className="w-12 h-12 bg-notion-sidebar rounded-full flex items-center justify-center mx-auto mb-3 text-stone-400">
                    <UploadCloud className="w-6 h-6" />
                </div>
                <h3 className="font-semibold text-lg text-notion-text">Import Leads</h3>
                <p className="text-xs text-stone-400 mt-1">Select a CSV file from your device.</p>
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mb-5 text-left">
                <p className="text-[10px] font-bold text-blue-800 uppercase mb-1">Headers Required</p>
                <p className="text-[10px] text-blue-600 leading-relaxed">First Name, Last Name, Email. Optional: Phone, Address, Tags.</p>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleInputChange}
                className="hidden"
            />
            <button
                onClick={handleBrowseClick}
                className="w-full border-2 border-dashed border-stone-300 rounded-xl p-6 text-center text-stone-500 font-medium active:bg-stone-50"
            >
                Choose File
            </button>

            {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-700">{error}</p>
                </div>
            )}

            <div className="mt-6 flex gap-3">
                <button onClick={onClose} className="flex-1 py-3 text-xs font-medium text-stone-500 bg-stone-100 rounded-lg">
                    Cancel
                </button>
            </div>
        </>
    );

    // Preview state
    const renderPreview = () => (
        <>
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-stone-500" />
                    <div>
                        <h3 className="font-semibold text-base text-notion-text">Preview</h3>
                        <p className="text-[10px] text-stone-400 truncate max-w-[180px]">{file?.name}</p>
                    </div>
                </div>
                <button onClick={handleReset} className="text-stone-400 hover:text-stone-600">
                    <X className="w-5 h-5" />
                </button>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-stone-50 rounded-lg p-2 text-center">
                    <p className="text-lg font-semibold text-stone-800">{parsedData?.length || 0}</p>
                    <p className="text-[9px] text-stone-500 uppercase">Total</p>
                </div>
                <div className="bg-green-50 rounded-lg p-2 text-center">
                    <p className="text-lg font-semibold text-green-700">{validationResult?.validLeads?.length || 0}</p>
                    <p className="text-[9px] text-green-600 uppercase">Valid</p>
                </div>
                <div className="bg-red-50 rounded-lg p-2 text-center">
                    <p className="text-lg font-semibold text-red-700">{validationResult?.invalidRows?.length || 0}</p>
                    <p className="text-[9px] text-red-600 uppercase">Invalid</p>
                </div>
            </div>

            {/* Errors */}
            {validationResult?.invalidRows?.length > 0 && (
                <div className="mb-4 max-h-24 overflow-y-auto">
                    <div className="bg-red-50 border border-red-100 rounded p-2">
                        <p className="text-[10px] text-red-700">
                            {validationResult.invalidRows.length} rows will be skipped due to errors
                        </p>
                    </div>
                </div>
            )}

            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-700">{error}</p>
                </div>
            )}

            <div className="flex gap-3">
                <button onClick={handleReset} className="flex-1 py-3 text-xs font-medium text-stone-500 bg-stone-100 rounded-lg">
                    Cancel
                </button>
                <button
                    onClick={handleImport}
                    disabled={!validationResult?.validLeads?.length}
                    className={`flex-1 py-3 text-xs font-medium rounded-lg ${validationResult?.validLeads?.length
                            ? 'bg-stone-800 text-white'
                            : 'bg-stone-200 text-stone-400'
                        }`}
                >
                    Import {validationResult?.validLeads?.length || 0}
                </button>
            </div>
        </>
    );

    // Importing state
    const renderImporting = () => (
        <div className="text-center py-6">
            <Loader className="w-8 h-8 text-stone-400 animate-spin mx-auto mb-4" />
            <h3 className="font-semibold text-base text-notion-text mb-2">Importing...</h3>

            <div className="w-full bg-stone-200 rounded-full h-2 overflow-hidden mb-2">
                <div
                    className="bg-stone-800 h-full transition-all duration-300"
                    style={{ width: `${progress.total ? (progress.current / progress.total) * 100 : 0}%` }}
                />
            </div>
            <p className="text-[10px] text-stone-400">
                {progress.current} of {progress.total}
            </p>
        </div>
    );

    // Results state
    const renderResults = () => (
        <>
            <div className="text-center mb-6">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${results?.failed === 0 ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'
                    }`}>
                    {results?.failed === 0 ? (
                        <CheckCircle className="w-6 h-6" />
                    ) : (
                        <AlertTriangle className="w-6 h-6" />
                    )}
                </div>
                <h3 className="font-semibold text-base text-notion-text">Import Complete</h3>
            </div>

            <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-green-50 rounded-lg p-2 text-center">
                    <p className="text-lg font-semibold text-green-700">{results?.created || 0}</p>
                    <p className="text-[9px] text-green-600 uppercase">Created</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-2 text-center">
                    <p className="text-lg font-semibold text-blue-700">{results?.updated || 0}</p>
                    <p className="text-[9px] text-blue-600 uppercase">Updated</p>
                </div>
                <div className="bg-red-50 rounded-lg p-2 text-center">
                    <p className="text-lg font-semibold text-red-700">{results?.failed || 0}</p>
                    <p className="text-[9px] text-red-600 uppercase">Failed</p>
                </div>
            </div>

            <div className="flex gap-3">
                <button onClick={handleReset} className="flex-1 py-3 text-xs font-medium text-stone-500 bg-stone-100 rounded-lg">
                    Import More
                </button>
                <button
                    onClick={onClose}
                    className="flex-1 py-3 bg-stone-800 text-white text-xs font-medium rounded-lg"
                >
                    Done
                </button>
            </div>
        </>
    );

    return (
        <div className="fixed inset-0 bg-stone-900/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white w-full max-w-sm rounded-xl shadow-sheet p-6 fade-in">
                {phase === 'idle' && renderIdle()}
                {phase === 'preview' && renderPreview()}
                {phase === 'importing' && renderImporting()}
                {phase === 'results' && renderResults()}
            </div>
        </div>
    );
};

export default MobileImportModal;
